import { createUserWithEmailAndPassword } from 'firebase/auth';
import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where,
    writeBatch
} from 'firebase/firestore';
import { COLLECTIONS } from '../utils/constants';
import { auth, db } from './firebase';
import axios from 'axios';

/**
 * Fetch all users with optional filters
 */
export const fetchUsers = async (roleFilter = null) => {
  try {
    const usersRef = collection(db, COLLECTIONS.USERS);
    let q = query(usersRef, orderBy('createdAt', 'desc'));

    if (roleFilter && roleFilter !== 'all') {
      q = query(usersRef, where('role', '==', roleFilter), orderBy('createdAt', 'desc'));
    }

    const querySnapshot = await getDocs(q);
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Listen to real-time users updates
 */
export const subscribeToUsers = (callback, roleFilter = null) => {
  const usersRef = collection(db, COLLECTIONS.USERS);
  let q = query(usersRef, orderBy('createdAt', 'desc'));

  if (roleFilter && roleFilter !== 'all') {
    q = query(usersRef, where('role', '==', roleFilter), orderBy('createdAt', 'desc'));
  }

  return onSnapshot(q, (snapshot) => {
    const users = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(users);
  });
};

/**
 * Fetch single user by ID
 */
export const fetchUserById = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw error;
  }
};

/**
 * Create new user
 */
export const createUser = async (userData) => {
  try {
    // Create auth user
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      userData.email,
      userData.password
    );

    const uid = userCredential.user.uid;

    // Create Firestore profile
    const userProfile = {
      uid,
      fullName: userData.fullName,
      email: userData.email,
      role: userData.role,
      classLevel: userData.classLevel || null,
      assignedSubjects: userData.assignedSubjects || [],
      profilePhoto: userData.profilePhoto || '',
      isActive: userData.isActive !== undefined ? userData.isActive : true,
      createdAt: serverTimestamp(),
      deviceToken: null,
    };

    // Use setDoc instead of updateDoc to create new document
    await setDoc(doc(db, COLLECTIONS.USERS, uid), userProfile);

    return { id: uid, ...userProfile };
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Update user
 */
export const updateUser = async (userId, userData) => {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    await updateDoc(userRef, {
      ...userData,
      updatedAt: serverTimestamp(),
    });
    return { id: userId, ...userData };
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

/**
 * Delete user
 */
export const deleteUserAccount = async (userId) => {
  try {
    // Fetch the admin's ID token to authenticate the request
    const idToken = await auth.currentUser.getIdToken();
    const apiUrl = import.meta.env.VITE_PAYMENT_API_URL || 'http://localhost:3000';
    
    // Call the Next.js API route instead of the Firebase Cloud Function
    await axios.post(`${apiUrl}/api/delete-user`, 
      { uid: userId }, 
      {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      }
    );

    // Delete Firestore document after Auth user is successfully deleted
    await deleteDoc(doc(db, COLLECTIONS.USERS, userId));

    return true;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Toggle user active status
 */
export const toggleUserStatus = async (userId, isActive) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
      isActive: !isActive,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Error toggling user status:', error);
    throw error;
  }
};

/**
 * Assign subjects to teacher
 */
export const assignSubjectsToTeacher = async (teacherId, subjects) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, teacherId), {
      assignedSubjects: subjects,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Error assigning subjects:', error);
    throw error;
  }
};

/**
 * Search users
 */
export const searchUsers = async (searchTerm, roleFilter = null) => {
  try {
    const users = await fetchUsers(roleFilter);
    
    const lowerSearch = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.fullName?.toLowerCase().includes(lowerSearch) ||
        user.email?.toLowerCase().includes(lowerSearch)
    );
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
};

/**
 * Helper to generate all equivalent subject aliases across SmartBrain ecosystem
 * (e.g. math_ss1 <-> mathematics_ss1 <-> mathematics <-> math)
 */
export const getSubjectAliases = (subjectId, options = {}) => {
  if (!subjectId || subjectId === '_class_package') return [subjectId];
  const clean = subjectId.toLowerCase().trim();
  const classMatch = clean.match(/_(ss1|ss2|ss3|jss1|jss2|jss3|utme)$/);
  const classSuffix = classMatch ? classMatch[0] : '';
  const base = classMatch ? clean.replace(classMatch[0], '') : clean;

  const aliasMap = {
    math: ['math', 'mathematics', 'maths'],
    mathematics: ['math', 'mathematics', 'maths'],
    maths: ['math', 'mathematics', 'maths'],
    fmaths: ['fmaths', 'further_mathematics', 'further_maths'],
    further_mathematics: ['fmaths', 'further_mathematics', 'further_maths'],
    further_maths: ['fmaths', 'further_mathematics', 'further_maths'],
    english: ['english', 'english_studies', 'english_language'],
    english_studies: ['english', 'english_studies', 'english_language'],
    english_language: ['english', 'english_studies', 'english_language'],
    physics: ['physics', 'phy'],
    phy: ['physics', 'phy'],
    chem: ['chem', 'chemistry'],
    chemistry: ['chem', 'chemistry'],
    bio: ['bio', 'biology'],
    biology: ['bio', 'biology'],
    agric: ['agric', 'agricultural_science'],
    agricultural_science: ['agric', 'agricultural_science'],
    govt: ['govt', 'government'],
    government: ['govt', 'government'],
    economics: ['economics', 'econ'],
    econ: ['economics', 'econ'],
    literature: ['literature', 'lit'],
    lit: ['literature', 'lit'],
    basic_science: ['basic_science', 'intermediate_science'],
    intermediate_science: ['basic_science', 'intermediate_science'],
    basic_tech: ['basic_tech', 'digital_technologies', 'ict'],
    ict: ['ict', 'digital_technologies', 'digital_tech'],
    digital_technologies: ['digital_technologies', 'ict', 'digital_tech'],
    digital_tech: ['digital_technologies', 'ict', 'digital_tech'],
    social_studies: ['social_studies', 'social_citizenship', 'civic'],
    social_citizenship: ['social_studies', 'social_citizenship', 'civic'],
    civic: ['civic', 'social_citizenship', 'social_studies'],
  };

  const baseAliases = aliasMap[base] || [base];
  const results = new Set();
  results.add(clean);

  const ALL_CLASSES = ['_ss1', '_ss2', '_ss3', '_jss1', '_jss2', '_jss3', '_utme'];
  baseAliases.forEach((alias) => {
    results.add(alias);
    if (classSuffix) {
      results.add(`${alias}${classSuffix}`);
    }
    if (options.allClasses) {
      ALL_CLASSES.forEach((cls) => {
        results.add(`${alias}${cls}`);
      });
    }
  });
  return Array.from(results);
};

/**
 * Grant Premium Access / Scholarship to Student
 */
export const grantPremiumAccess = async (userId, options = 12) => {
  try {
    let months = 12;
    let tier = 'class';
    let accessMode = 'monthly';
    let selectedTerm = 1;
    let subjectIds = [];

    if (typeof options === 'object' && options !== null) {
      months = options.months || 12;
      tier = options.tier || 'class';
      accessMode = options.accessMode || 'monthly';
      selectedTerm = options.selectedTerm || 1;
      subjectIds = options.subjectIds || [];
    } else if (typeof options === 'number') {
      months = options;
    }

    const futureDate = new Date();
    futureDate.setMonth(futureDate.getMonth() + months);
    const unlockedTerms = accessMode === 'term' ? [Number(selectedTerm)] : [];

    const batch = writeBatch(db);

    // 1. Sync User doc premium flag & scholarship details
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      batch.update(userRef, {
        isPremium: true,
        scholarship: {
          active: true,
          tier: tier === 'class' ? 'class' : (tier === 'single' ? 'single' : 'bundle'),
          accessMode,
          unlockedTerms,
          grantedAt: serverTimestamp(),
          expiresAt: futureDate,
        },
        updatedAt: serverTimestamp(),
      });
    } catch (_) {}

    // 2. Grant class package or subject-level subscriptions with mobile compatibility aliases
    if (tier === 'class') {
      const subscriptionRef = doc(db, 'subscriptions', userId, 'subjects', '_class_package');
      batch.set(subscriptionRef, {
        classPackageActive: true,
        classPackageExpiresAt: futureDate,
        grantedByAdmin: true,
        enrolledAt: serverTimestamp(),
        accessMode,
        unlockedTerms,
      }, { merge: true });
    } else {
      if (!subjectIds || subjectIds.length === 0) {
        throw new Error('No subjects selected for the scholarship.');
      }

      for (const subjectId of subjectIds) {
        const aliases = getSubjectAliases(subjectId);
        for (const aliasId of aliases) {
          const subRef = doc(db, 'subscriptions', userId, 'subjects', aliasId);
          batch.set(subRef, {
            status: 'active',
            enrolledAt: serverTimestamp(),
            expiresAt: futureDate,
            tier: tier === 'single' ? 'single' : 'bundle',
            unlockedTerms,
            progress: 0,
            grantedByAdmin: true,
            canonicalSubjectId: subjectId,
          }, { merge: true });
        }
      }
    }

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Error granting premium access:', error);
    throw error;
  }
};

/**
 * Fetch all active scholarships / subjects for a student
 */
export const fetchStudentScholarships = async (userId) => {
  try {
    const subjectsRef = collection(db, 'subscriptions', userId, 'subjects');
    const querySnapshot = await getDocs(subjectsRef);

    let classPackage = null;
    const subjects = [];
    const seenCanonical = new Set();
    const missingAliasesToWrite = [];
    const now = new Date();

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      const id = docSnap.id;

      if (id === '_class_package') {
        const expiresAt = data.classPackageExpiresAt?.toDate 
          ? data.classPackageExpiresAt.toDate() 
          : (data.classPackageExpiresAt ? new Date(data.classPackageExpiresAt) : null);
        const isActive = data.classPackageActive === true && (!expiresAt || expiresAt > now);

        if (isActive) {
          classPackage = {
            id,
            name: 'Full Class Package',
            accessMode: data.accessMode || 'monthly',
            unlockedTerms: Array.isArray(data.unlockedTerms) ? data.unlockedTerms : [],
            expiresAt,
            grantedByAdmin: data.grantedByAdmin ?? true,
          };
        }
      } else {
        const expiresAt = data.expiresAt?.toDate 
          ? data.expiresAt.toDate() 
          : (data.expiresAt ? new Date(data.expiresAt) : null);
        const isActive = (data.status === 'active' || (!data.status && data.grantedByAdmin)) && (!expiresAt || expiresAt > now);

        if (isActive) {
          // Normalize to canonical group to avoid displaying multiple duplicate rows in the Admin UI
          const cleanId = id.toLowerCase().trim();
          const classMatch = cleanId.match(/_(ss1|ss2|ss3|jss1|jss2|jss3|utme)$/);
          const classSuffix = classMatch ? classMatch[0] : '';
          const base = classMatch ? cleanId.replace(classMatch[0], '') : cleanId;
          const canonicalBase = (base === 'mathematics' || base === 'maths') ? 'math'
            : (base === 'further_mathematics' || base === 'further_maths') ? 'fmaths'
            : (base === 'chemistry') ? 'chem'
            : (base === 'biology') ? 'bio'
            : (base === 'english_studies' || base === 'english_language') ? 'english'
            : (base === 'government') ? 'govt'
            : (base === 'economics') ? 'economics'
            : (base === 'phy') ? 'physics'
            : base;

          const canonicalKey = canonicalBase;

          // Check if this active subscription needs alias backfilling for mobile compatibility
          const expectedAliases = getSubjectAliases(id);
          const existingIds = querySnapshot.docs.map(d => d.id);
          for (const alias of expectedAliases) {
            if (!existingIds.includes(alias)) {
              missingAliasesToWrite.push({
                alias,
                data: {
                  ...data,
                  status: 'active',
                  canonicalSubjectId: id,
                  enrolledAt: data.enrolledAt || serverTimestamp(),
                  expiresAt: data.expiresAt || new Date(Date.now() + 365 * 86400000),
                  tier: data.tier || 'single',
                  unlockedTerms: Array.isArray(data.unlockedTerms) ? data.unlockedTerms : [],
                  grantedByAdmin: true,
                }
              });
            }
          }

          if (!seenCanonical.has(canonicalKey)) {
            seenCanonical.add(canonicalKey);
            subjects.push({
              id,
              canonicalId: canonicalKey,
              name: id,
              tier: data.tier || 'single',
              unlockedTerms: Array.isArray(data.unlockedTerms) ? [...data.unlockedTerms] : [],
              expiresAt,
              grantedByAdmin: data.grantedByAdmin ?? false,
            });
          } else {
            // Merge terms & expiration into existing card without duplicate row
            const existing = subjects.find(s => s.canonicalId === canonicalKey);
            if (existing) {
              if (Array.isArray(data.unlockedTerms) && data.unlockedTerms.length > 0) {
                const merged = new Set([...(existing.unlockedTerms || []), ...data.unlockedTerms]);
                existing.unlockedTerms = Array.from(merged).sort((a, b) => a - b);
              }
              if (expiresAt && (!existing.expiresAt || expiresAt > existing.expiresAt)) {
                existing.expiresAt = expiresAt;
              }
              if (data.tier === 'bundle') {
                existing.tier = 'bundle';
              }
              if (!existing.id.includes('_') && id.includes('_')) {
                existing.id = id;
              }
            }
          }
        }
      }
    });

    // Auto-heal missing mobile compatibility aliases in background if found
    if (missingAliasesToWrite.length > 0) {
      try {
        const healBatch = writeBatch(db);
        for (const item of missingAliasesToWrite) {
          const ref = doc(db, 'subscriptions', userId, 'subjects', item.alias);
          healBatch.set(ref, item.data, { merge: true });
        }
        healBatch.commit().catch(err => console.warn('Background alias sync warning:', err));
      } catch (_) {}
    }

    const hasScholarship = !!classPackage || subjects.length > 0;

    return {
      hasScholarship,
      classPackage,
      subjects,
      totalActive: (classPackage ? 1 : 0) + subjects.length,
    };
  } catch (error) {
    console.error('Error fetching student scholarships:', error);
    return { hasScholarship: false, classPackage: null, subjects: [], totalActive: 0 };
  }
};

/**
 * Check Premium Access Status
 */
export const checkPremiumAccess = async (userId) => {
  try {
    const details = await fetchStudentScholarships(userId);
    return details.hasScholarship;
  } catch (error) {
    console.error('Error checking premium access:', error);
    return false;
  }
};

/**
 * Revoke specific scholarship (a single subject or the class package)
 */
export const revokeSpecificScholarship = async (userId, subjectId) => {
  try {
    const batch = writeBatch(db);
    if (subjectId === '_class_package') {
      const subRef = doc(db, 'subscriptions', userId, 'subjects', '_class_package');
      batch.update(subRef, {
        classPackageActive: false,
        revokedAt: serverTimestamp(),
      });
    } else {
      const allAliases = getSubjectAliases(subjectId, { allClasses: true });
      for (const alias of allAliases) {
        const subRef = doc(db, 'subscriptions', userId, 'subjects', alias);
        batch.delete(subRef);
      }
    }
    await batch.commit();

    // Check if user has any active scholarships left; if none, clear isPremium
    try {
      const details = await fetchStudentScholarships(userId);
      if (!details.hasScholarship) {
        await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
          isPremium: false,
          scholarship: null,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (_) {}

    return true;
  } catch (error) {
    console.error(`Error revoking scholarship for ${subjectId}:`, error);
    throw error;
  }
};

/**
 * Revoke a specific term (e.g. Term 1, Term 2, or Term 3) from a subject or class package
 */
export const revokeTermFromScholarship = async (userId, subjectId, termNumber) => {
  try {
    if (subjectId === '_class_package') {
      const subRef = doc(db, 'subscriptions', userId, 'subjects', '_class_package');
      const subDoc = await getDoc(subRef);
      if (subDoc.exists()) {
        const data = subDoc.data();
        const currentTerms = Array.isArray(data.unlockedTerms) ? data.unlockedTerms : [];
        const updatedTerms = currentTerms.filter((t) => Number(t) !== Number(termNumber));
        if (updatedTerms.length === 0 && data.accessMode === 'term') {
          await updateDoc(subRef, {
            classPackageActive: false,
            unlockedTerms: [],
            revokedAt: serverTimestamp(),
          });
        } else {
          await updateDoc(subRef, {
            unlockedTerms: updatedTerms,
            updatedAt: serverTimestamp(),
          });
        }
      }
    } else {
      const allAliases = getSubjectAliases(subjectId, { allClasses: true });
      const batch = writeBatch(db);

      for (const alias of allAliases) {
        const subRef = doc(db, 'subscriptions', userId, 'subjects', alias);
        const subDoc = await getDoc(subRef);
        if (subDoc.exists()) {
          const data = subDoc.data();
          const currentTerms = Array.isArray(data.unlockedTerms) ? data.unlockedTerms : [];
          const updatedTerms = currentTerms.filter((t) => Number(t) !== Number(termNumber));
          if (updatedTerms.length === 0) {
            batch.delete(subRef);
          } else {
            batch.update(subRef, {
              unlockedTerms: updatedTerms,
              updatedAt: serverTimestamp(),
            });
          }
        }
      }
      await batch.commit();
    }
    return true;
  } catch (error) {
    console.error(`Error revoking term ${termNumber} for ${subjectId}:`, error);
    throw error;
  }
};

/**
 * Revoke All Premium Access (Full Class Package and All Individual Subjects)
 */
export const revokePremiumAccess = async (userId) => {
  try {
    const subjectsRef = collection(db, 'subscriptions', userId, 'subjects');
    const querySnapshot = await getDocs(subjectsRef);

    const batch = writeBatch(db);

    querySnapshot.forEach((docSnap) => {
      const subRef = doc(db, 'subscriptions', userId, 'subjects', docSnap.id);
      if (docSnap.id === '_class_package') {
        batch.update(subRef, {
          classPackageActive: false,
          revokedAt: serverTimestamp(),
        });
      } else {
        batch.delete(subRef);
      }
    });

    // Reset user doc premium status
    const userRef = doc(db, COLLECTIONS.USERS, userId);
    batch.update(userRef, {
      isPremium: false,
      scholarship: null,
      updatedAt: serverTimestamp(),
    });

    await batch.commit();
    return true;
  } catch (error) {
    console.error('Error revoking all premium access:', error);
    throw error;
  }
};

export default {
  fetchUsers,
  subscribeToUsers,
  fetchUserById,
  createUser,
  updateUser,
  deleteUserAccount,
  toggleUserStatus,
  assignSubjectsToTeacher,
  searchUsers,
  getSubjectAliases,
  grantPremiumAccess,
  checkPremiumAccess,
  fetchStudentScholarships,
  revokeSpecificScholarship,
  revokeTermFromScholarship,
  revokePremiumAccess,
};
